/**
 * Assessment Helpers
 * Utility functions for looking up & mapping saved risk assessments.
 * Used by C2Invitations and other components that need assessment data.
 */

import type { RiskRatingUpper, SavedAssessment } from '../types/risk-assessment.types';
import { logger } from './logger';
import { parseUpperRiskRating, parseWireRiskLevelToUpper } from './riskRating';

export const STORAGE_KEY_ASSESSMENTS = 'arcaml:assessments';

/**
 * Map a raw localStorage / history entry to a typed SavedAssessment.
 * Handles both the new format (`clientName`) and the legacy format (`c2Name`).
 */
/**
 * AL-442 #2: this used to substitute 'MEDIUM' for an unreadable rating — a
 * different guess from mapBackendRiskLevel's 'LOW', twenty lines away in the
 * same file, for the same unknown value.
 *
 * It returns null instead, and callers drop the entry. Not a throw: the database
 * constraint does not cover the browser's cache, so a stale entry from an older
 * build is genuinely reachable here, and both callers wrap the read in a
 * try/catch returning []. A throw would therefore empty the user's whole history
 * because of one bad row, and keep doing so until they cleared storage. Dropping
 * the single unreadable entry is the sensible thing available, which is the
 * carve-out AL-442 permits.
 */
export function mapHistoryToAssessment(
  entry: Record<string, unknown>,
): SavedAssessment | null {
  const rating = parseUpperRiskRating(String(entry.riskRating ?? ''));
  if (!rating) {
    logger.warn('[assessmentHelpers] Dropping cached assessment with unreadable risk rating', {
      id: String(entry.id ?? ''),
      received: String(entry.riskRating ?? ''),
    });
    return null;
  }
  return {
    id: String(entry.id || ''),
    clientName: String(entry.clientName || entry.c2Name || ''),
    clientRef: String(entry.clientRef || ''),
    entityAbn: String(entry.entityAbn || ''),
    riskRating: rating,
    savedAt: String(entry.savedAt || entry.completedAt || ''),
    profession: String(entry.profession || entry.c1Type || ''),
    progress: Number(entry.progress ?? 100),
    c2EntityType: String(entry.c2EntityType || ''),
    contactPerson: String(entry.contactPerson || ''),
    contactPhone: String(entry.contactPhone || ''),
  };
}

/**
 * Look up a saved assessment by C2 entity name (case-insensitive, partial match).
 * Reads from the localStorage assessments cache.
 */
export function findAssessment(orgName: string | undefined | null): SavedAssessment | null {
  if (!orgName || !orgName.trim()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ASSESSMENTS);
    if (!raw) return null;
    const list: SavedAssessment[] = (JSON.parse(raw) as Record<string, unknown>[])
      .map(mapHistoryToAssessment)
      // AL-442: entries whose rating could not be read are dropped, not guessed.
      .filter((a): a is SavedAssessment => a !== null);
    const needle = orgName.trim().toLowerCase();
    return (
      list.find(
        (a) =>
          a.clientName.toLowerCase().includes(needle) ||
          needle.includes(a.clientName.toLowerCase()),
      ) || null
    );
  } catch {
    return null;
  }
}

/**
 * Return all completed assessments from the localStorage cache.
 */
export function getAllAssessments(): SavedAssessment[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ASSESSMENTS);
    if (!raw) return [];
    return (JSON.parse(raw) as Record<string, unknown>[])
      .map(mapHistoryToAssessment)
      // AL-442: as above — an unreadable rating drops the entry rather than
      // inventing one for it.
      .filter((a): a is SavedAssessment => a !== null);
  } catch {
    return [];
  }
}

/**
 * Find an assessment in an in-memory list by entity name (case-insensitive partial match).
 * Only searches the provided in-memory list — never reads from localStorage.
 */
export function findAssessmentInList(
  orgName: string | undefined | null,
  assessments: SavedAssessment[],
): SavedAssessment | null {
  if (!orgName || !orgName.trim()) return null;
  const needle = orgName.trim().toLowerCase();
  return (
    assessments.find(
      (a) =>
        a.clientName.toLowerCase().includes(needle) ||
        needle.includes(a.clientName.toLowerCase()),
    ) ?? null
  );
}

/**
 * Map a backend `riskLevel` string ("High Risk" | "Med Risk" | "Low Risk")
 * to the upper-case rating used by the frontend.
 *
 * AL-442 #1: this used to return 'LOW' for anything it did not recognise, so an
 * unreadable rating silently became the least severe one and every downstream
 * consumer then behaved correctly on a wrong input. It now throws.
 *
 * The throw is unreachable for values from risk_assessments.risk_level, which
 * a2d54cc constrained to the three canonical strings. That is the point: if the
 * assumption breaks, it should break loudly rather than downgrade a rating.
 */
export function mapBackendRiskLevel(riskLevel: string): RiskRatingUpper {
  return parseWireRiskLevelToUpper(riskLevel);
}
